import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { CompleteEnrollmentRequest, MasterClientProductViewDto } from '../../../models/api-data-contracts';
import { States } from '../../../common/constants';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../../../common/error-dialog';
import { paymentFrequencyName, calculatePlanRate, calculatePlanRateWithDiscount, enrollmentContactEmail } from '../../../common/helpers';
import { TurnstileTokenService } from '../../../services/turnstile-token.service';
import { PlansSummaryComponent } from '../plans-summary/plans-summary.component';
import { BillingTypes } from '../../../common/enums';

@Component({
  selector: 'app-checkout',
  imports: [
    CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatCheckboxModule, MatButtonModule, MatSelectModule, MatIconModule, NgxMaskDirective, NgxMaskPipe, PlansSummaryComponent
  ],
  host: {
    class: 'w-full'
  },
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss'
})
export class CheckoutComponent implements OnInit {
  @Input({required: true}) memberData: any; // EnrollmentDraft data
  @Input({required: true}) validationToken: string;
  @Input({required: true}) sessionToken: string;
  @Input({required: true}) products: any[]; // MasterClientProductViewDto[]
  @Input({required: true}) discountApplied: number = 0;
  @Input() billingType: number;
  @Output() complete = new EventEmitter<CompleteEnrollmentRequest>();
  @Output() back = new EventEmitter<void>();

  get isPayrollDeducted(): boolean {
    return this.billingType === BillingTypes.PayrollDeducted;
  }

  get displayEmail(): string {
    return enrollmentContactEmail(this.memberData);
  }

  get headerTitle(): string {
    return this.isPayrollDeducted ? 'Complete Enrollment' : 'Payment Information';
  }

  get headerSubtitle(): string {
    return this.isPayrollDeducted
      ? 'Review your information and submit your enrollment'
      : 'Complete your enrollment by providing payment details';
  }

  form: FormGroup;
  readonly states = States;
  readonly maxDate = new Date();
  readonly currentYear = new Date().getFullYear();
  termsExpanded = false;
  submitted = false;

  get minDate() {
    const mDate = new Date();
    mDate.setFullYear(mDate.getFullYear() - 125);
    return mDate;
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private turnstileTokenService: TurnstileTokenService
  ) {}

  ngOnInit(): void {
    if (this.isPayrollDeducted) {
      this.form = this.fb.group({
        acceptTerms: [false, Validators.requiredTrue]
      });
    } else {
      this.form = this.fb.group({
        isBillingSameAsHome: [true],
        billingAddress: this.createAddressGroup(),
        creditCard: this.createCardGroup(),
        acceptTerms: [false, Validators.requiredTrue]
      });
      this.syncBillingAddress();
    }
    this.initializeTurnstile();
  }

  private initializeTurnstile() {
    (window as any).onTurnstileSuccess = (token: string) => {
      this.turnstileTokenService.updateToken(token);
    }
    (window as any).onTurnstileError = () => {
      this.turnstileTokenService.updateToken(null);
    }
    (window as any).onTurnstileExpired = () => {
      this.turnstileTokenService.updateToken(null);
    }

    setTimeout(() => {
      const container = document.getElementById('turnstile-checkout');
      if (container && (window as any).turnstile) {
        container.innerHTML = '';
        const widgetId = (window as any).turnstile.render('#turnstile-checkout', {
          sitekey: '0x4AAAAAABDQhK0UT12Ulx-d',
          callback: (window as any).onTurnstileSuccess,
          'expired-callback': (window as any).onTurnstileExpired,
          'error-callback': (window as any).onTurnstileError,
          theme: 'light'
        });
        this.turnstileTokenService.setWidgetId(widgetId);
      }
    }, 100);
  }

  createAddressGroup() {
    return this.fb.group({
      address: ['', Validators.required],
      zipCode: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required]
    });
  }

  createCardGroup() {
    return this.fb.group({
      cardNumber: ['', Validators.required],
      cardHolderName: ['', Validators.required],
      expiryMonth: ['', [Validators.required, Validators.max(12), Validators.min(1)]],
      expiryYear: ['', [Validators.required, Validators.min((new Date()).getFullYear())]],
      cvv: ['', Validators.required],
    });
  }

  syncBillingAddress() {
    this.form.get('billingAddress')?.disable();
    this.form.get('isBillingSameAsHome')?.valueChanges.subscribe(isSame => {
      if (isSame) {
        this.form.get('billingAddress')?.disable();
      } else {
        this.form.get('billingAddress')?.enable();
      }
    });
  }

  planRate(plan: MasterClientProductViewDto){
    return calculatePlanRate([plan], this.memberData.paymentFrequency ?? 1);
  }

  planRateWithDiscount(plan: MasterClientProductViewDto) {
    return calculatePlanRateWithDiscount([plan], this.memberData.paymentFrequency ?? 1, this.discountApplied);
  }

  totalPrice() {
    return calculatePlanRate(this.products, this.memberData.paymentFrequency ?? 1);
  }

  totalPriceWithDiscount() {
    return calculatePlanRateWithDiscount(this.products, this.memberData.paymentFrequency ?? 1, this.discountApplied);
  }

  paymentFrequency(){
    return paymentFrequencyName(this.memberData.paymentFrequency ?? 1)
  }

  async onSubmit() {
    this.submitted = true;
    if (this.form.invalid) {
      this.markAllAsTouched(this.form);
      return;
    }

    try {
      await this.turnstileTokenService.ensureValidToken();

      const request: CompleteEnrollmentRequest = {
        validationToken: this.validationToken,
        sessionToken: this.sessionToken
      };

      if (!this.isPayrollDeducted) {
        const formValue = this.form.value;
        request.isBillingSameAsHome = formValue.isBillingSameAsHome;
        request.billingAddress = formValue.isBillingSameAsHome ? this.memberData.homeAddress : formValue.billingAddress;
        request.creditCard = formValue.creditCard;
      }

      this.complete.emit(request);

    } catch (error) {
      this.dialog.open(ErrorDialogComponent, {
        data: { message: 'Security verification failed. Please try again.' }
      });
      this.turnstileTokenService.reset();
    }
  }

  onBack() {
    this.back.emit();
  }

  toggleTerms() {
    this.termsExpanded = !this.termsExpanded;
  }

  private markAllAsTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup) {
        this.markAllAsTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }
}
